import React from 'react';
import { Grid, Card, Group, Text, useMantineTheme, useMantineColorScheme } from '@mantine/core';
import {
  IconShield,
  IconLicense,
  IconUsers,
  IconPackage,
  IconEye,
  IconStar,
  IconCalendarCheck,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { ProductTotals } from '@/Api';

interface MasterAdminStatsProps {
  appsCount: number;
  licensesCount: number;
  totalUsers: number;
  adminsCount: number;
  activeSubscriptionsCount: number;
  /** null while loading or when the server has no totals endpoint. */
  productTotals: ProductTotals | null;
}

export const MasterAdminStats: React.FC<MasterAdminStatsProps> = ({
  appsCount,
  licensesCount,
  totalUsers,
  adminsCount,
  activeSubscriptionsCount,
  productTotals,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const statsCard = {
    backgroundColor: isDark ? theme.colors.dark[6] : 'white',
    border: `1px solid ${isDark ? theme.colors.dark[4] : '#e5e7eb'}`,
    borderRadius: '12px',
    padding: '20px',
    boxShadow: isDark ? '0 1px 3px 0 rgba(0, 0, 0, 0.3)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
  };

  const format = (value: number | undefined) =>
    value === undefined ? '–' : value.toLocaleString(i18n.language);

  // Row 1: platform reach (people, content, engagement). Row 2: app business (apps, licenses, paid seats, admins).
  const stats = [
    { icon: IconUsers, color: '#f59e0b', value: totalUsers, label: t('masterAdmin.users.totalUsers') },
    { icon: IconPackage, color: '#0ea5e9', value: productTotals?.products, label: t('masterAdmin.stats.products') },
    { icon: IconEye, color: '#6366f1', value: productTotals?.views, label: t('masterAdmin.stats.views') },
    { icon: IconStar, color: '#eab308', value: productTotals?.stars, label: t('masterAdmin.stats.favorites') },
    { icon: IconShield, color: '#3b82f6', value: appsCount, label: t('masterAdmin.apps.title') },
    { icon: IconLicense, color: '#10b981', value: licensesCount, label: t('masterAdmin.licenses.title') },
    {
      icon: IconCalendarCheck,
      color: '#14b8a6',
      value: activeSubscriptionsCount,
      label: t('masterAdmin.stats.activeSubscriptions'),
    },
    { icon: IconShield, color: '#8b5cf6', value: adminsCount, label: t('masterAdmin.admins.title') },
  ];

  return (
    <Grid mb="xl">
      {stats.map(({ icon: Icon, color, value, label }) => (
        <Grid.Col key={label} span={{ base: 12, sm: 6, md: 3 }}>
          <Card style={statsCard}>
            <Group>
              <Icon size={24} color={color} />
              <div>
                <Text size="lg" fw={600} c={isDark ? 'gray.2' : 'dark.7'}>
                  {format(value)}
                </Text>
                <Text size="sm" c="dimmed">
                  {label}
                </Text>
              </div>
            </Group>
          </Card>
        </Grid.Col>
      ))}
    </Grid>
  );
};
